// Actions
import { inviteBot, removeBot, getMeetingBotForWorkspace } from 'Shared/actions/WorkspaceMeetingMode.actions';

// Types
import type { BotAttributes } from 'Shared/types/WorkspaceMeetingMode.type';
import { Feature } from 'Shared/types/Organization.type';

// Hooks
import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useWorkspace } from './use-workspace';
import { useFeature } from '../hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Selectors
import { getBotByWorkspaceId } from 'Shared/selectors/WorkspaceMeetingMode.selectors';

// API
import { getMeetingSummary, getMeetingSummariesByWorkspaceSlug } from 'Shared/api/BotManagement.api';

export const useMeetingBot = () => {
  const dispatch = useDispatch();
  const workspace = useWorkspace();
  const { isActive: hasMeetingBotFeatureGate } = useFeature(Feature.meetingBot);
  const { enablePiCompanion } = useFlags();
  const isMeetingBotEnabled = hasMeetingBotFeatureGate && enablePiCompanion;

  const bot: BotAttributes | undefined = useSelector(state =>
    getBotByWorkspaceId(state, { workspaceId: workspace.id })
  );

  const invite = useCallback(
    (meetingUrl: string) => {
      return dispatch(inviteBot(workspace.id, meetingUrl));
    },
    [dispatch, workspace.id]
  );

  const remove = useCallback(
    (recallBotId: string) => {
      return dispatch(removeBot(workspace.id, recallBotId));
    },
    [dispatch]
  );

  const fetchMeetingBot = useCallback(() => {
    return dispatch(getMeetingBotForWorkspace(workspace.id));
  }, [dispatch, workspace.id]);

  const fetchMeetingSummary = useCallback(
    async (refresh = false) => {
      if (!bot) return null;
      return (await getMeetingSummary(bot.recallBotId, { workspace_slug: workspace.id, refresh })).data;
    },
    [bot, workspace.id]
  );

  const fetchMeetingSummaryHistory = useCallback(async () => {
    return await getMeetingSummariesByWorkspaceSlug(workspace.id);
  }, [workspace.id]);

  const meetingBotIsInProgress = bot && !bot?.isComplete;

  return {
    bot,
    meetingBotIsInProgress,
    invite,
    remove,
    fetchMeetingBot,
    fetchMeetingSummary,
    fetchMeetingSummaryHistory,
    isMeetingBotEnabled,
  };
};
